import React, { FC } from 'react';

import { AvatarGroup, AvatarProps, Avatar as MantineAvatar, Tooltip, TooltipProps } from '@mantine/core';

import { Manufacturer, User } from 'models';
import { ImageKitHelpers } from 'helpers/ImageKitHelpers';

import { useCompanyProfile } from 'hooks/use-company-profile';
import { useFile } from 'hooks/use-file';
import { useUser } from 'hooks/use-user';

const Avatar: FC<
    AvatarProps & {
        user?: User | User['id'] | null;
        company?: Manufacturer | Manufacturer['id'] | null;
        name?: string;
        image?: string;
        tooltip?: boolean | Partial<TooltipProps>;
        onClick?: () => void;
    }
> = ({ user, company, name, image, size = 'sm', ...props }) => {
    if (name) {
        return <AbstractAvatar name={name} image={image} size={size} {...props} />;
    }

    if (company) {
        return typeof company === 'string' ? (
            <AsyncCompanyAvatar companyId={company} size={size} {...props} />
        ) : (
            <CompanyAvatar company={company} size={size} {...props} />
        );
    }

    if (user) {
        return typeof user === 'string' ? (
            <AsyncUserAvatar userId={user} size={size} {...props} />
        ) : (
            <UserAvatar user={user} size={size} {...props} />
        );
    }

    return null;
};

const AsyncUserAvatar: FC<
    AvatarProps & {
        userId: User['id'];
    }
> = ({ userId, ...props }) => {
    const { user, isLoading } = useUser(userId);

    if (isLoading) {
        return <AbstractAvatar name=" " color="gray" {...props} />;
    }

    return user ? <UserAvatar user={user} {...props} /> : null;
};

const UserAvatar: FC<
    AvatarProps & {
        user: User;
    }
> = ({ user, ...props }) => <AbstractAvatar name={user.name ?? user.email} image={user.profileImage} {...props} />;

const AsyncCompanyAvatar: FC<
    AvatarProps & {
        companyId: User['id'];
    }
> = ({ companyId, ...props }) => {
    const { company, isLoading } = useCompanyProfile(companyId);

    if (isLoading) {
        return <AbstractAvatar name=" " variant="filled" bg="gray.0" {...props} />;
    }

    return company ? <CompanyAvatar company={company} {...props} /> : null;
};

const CompanyAvatar: FC<
    AvatarProps & {
        company: Manufacturer;
    }
> = ({ company, ...props }) => (
    <AbstractAvatar name={company.name} image={company.logos.small as string} imageContain {...props} />
);

const AbstractAvatar: FC<
    AvatarProps & {
        name: string;
        image?: string | null;
        imageContain?: boolean;
        tooltip?: boolean | Partial<TooltipProps>;
    }
> = ({ name, image, imageContain = false, tooltip, ...props }) => {
    const letter = name[0].toUpperCase();
    const color = getBackgroundColor(letter);

    const { file } = useFile(image);

    const avatar = (
        <MantineAvatar
            variant="light"
            color={color}
            src={
                file
                    ? ImageKitHelpers.getSrcFromFile(file, { width: 100, height: 100, disableCrop: imageContain })
                    : undefined
            }
            styles={{
                image: {
                    objectFit: imageContain ? 'contain' : 'cover',
                    padding: imageContain ? 3 : 0,
                },
            }}
            style={{
                border: file ? '1px solid rgba(0, 0, 0, .1)' : undefined,
                backgroundColor: file && imageContain ? 'white' : undefined,
            }}
            {...props}
        >
            {!file && letter}
        </MantineAvatar>
    );

    return tooltip ? (
        <Tooltip
            label={name}
            position="bottom"
            withArrow
            withinPortal={false}
            {...(typeof tooltip === 'object' ? tooltip : {})}
        >
            {avatar}
        </Tooltip>
    ) : (
        avatar
    );
};

const COLORS = ['red', 'grape', 'violet', 'blue', 'cyan', 'teal', 'indigo', 'orange', 'yellow'];

export const getBackgroundColor = (letter: string) => {
    const index = letter.toLowerCase().charCodeAt(0) % COLORS.length;

    return COLORS[index];
};

export { Avatar, AvatarGroup };
